import { Component, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { ApiService } from '../../services/api/api.service';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    FormControl,
} from '@angular/forms';
import { FileUploadModule, FileUploadValidators } from '@iplab/ngx-file-upload';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../../common/confirm-dialog/confirn-dialog.component';
import { VulnerabilityTracking } from '../../interfaces/VulnerabilityTracking';
import { VulnerabilityTrackingResponse } from '../../interfaces/VulnerabilityTrackingResponse';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgForOf } from '@angular/common';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { GlobalConstants } from '../../common/global-constants';

@Component({
    selector: 'app-v-manage-vulnerability',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        RouterLink,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        NgForOf,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
        FormsModule,
        FileUploadModule,
        ReactiveFormsModule,
    ],
    templateUrl: './v-manage-vulnerability.component.html',
    styleUrl: './v-manage-vulnerability.component.scss',
})
export class VManageVulnerabilityComponent {
    displayedColumns: string[] = [
        'task',
        'note',
        'trackingCreationDate',
        'trackingResolutionDate',
        'taskType',
        'assignedUserName',
        'file',
        'action',
    ];
    data: VulnerabilityTracking[] = [];
    dataSource = new MatTableDataSource<VulnerabilityTracking>(this.data);
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;

    vulnerabilityTrackingForm: FormGroup;

    fileControl = new FormControl<File[]>([], [
            FileUploadValidators.filesLimit(1),
            FileUploadValidators.accept(['.gif','.jpg','.png','.zip','.pdf']),
        ]);

    // Vulnerability Assessment Id
    vulnerabilityAssessmentId: string;

    // Vulnerability Assessment
    vulnerabilityAssessment: VulnerabilityAssessment;

    // Vulnerability Assessment Result Id
    vulnerabilityAssessmentResultId: string;

    // Vulnerability Tracking Id
    vulnerabilityTrackingId: string = '';

    // Array of type of tasks
    taskTypes: string[] = [
        'Mitigacion',
        'Remediacion',
        'Retest',
        'Seguimiento',
    ];

    readonly dialog = inject(MatDialog);

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    // isToggled
    isToggled = false;

    //isWorking
    isWorking = false;

    internal = GlobalConstants.internal;
    specialist = GlobalConstants.specialist;
    supervisor = GlobalConstants.supervisor;
    manager = GlobalConstants.manager;

    constructor(
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private route: ActivatedRoute,
        private fb: FormBuilder
    ) {
        this.vulnerabilityTrackingForm = this.fb.group({
            task: ['', [Validators.required, Validators.maxLength(400)]],
            note: ['', []],
            taskType: ['', [Validators.required, Validators.maxLength(18)]],
            trackingCreationDate: [{ value: '', disabled: true }, []],
            trackingResolutionDate: [{ value: '', disabled: true }, []],
            file: this.fileControl,
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.vulnerabilityAssessmentId =
                params['vulnerabilityAssessmentId'];

            this.vulnerabilityAssessmentResultId =
                params['vulnerabilityAssessmentResultId'];

            this.apiService
                .getVulnerabilityAssessmentById(
                    this.vulnerabilityAssessmentId,
                    this.roleName == GlobalConstants.internal
                )
                .subscribe((data: VulnerabilityAssessmentResponse) => {
                    this.vulnerabilityAssessment = data.vulnerabilityAssessment;
                });

            // If the user changes the sort order, reset back to the first page.
            this.sort.sortChange.subscribe(
                () => (this.paginator.pageIndex = 0)
            );

            merge(this.sort.sortChange, this.paginator.page)
                .pipe(
                    startWith({}),
                    switchMap(() => {
                        this.isLoadingResults = true;
                        return this.apiService
                            .getVulnerabilityTrackingByVulnerabilityResult(
                                this.vulnerabilityAssessmentResultId,
                                this.paginator.pageIndex,
                                this.paginator.pageSize,
                                this.sort.active,
                                this.sort.direction
                            )
                            .pipe(catchError(() => observableOf(null)));
                    }),
                    map((data) => {
                        // Flip flag to show that loading has finished.
                        this.isLoadingResults = false;
                        //this.isRateLimitReached = data === null;

                        if (data === null) {
                            return [];
                        }

                        // Only refresh the result length if there is new data. In case of rate
                        // limit errors, we do not want to reset the paginator to zero, as that
                        // would prevent users from re-triggering requests.
                        this.totalItems = data.totalElements;
                        return data.vulnerabilitiesTracking;
                    })
                )
                .subscribe((data) => (this.dataSource.data = data));
        });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.vulnerabilityTrackingId = '';
        this.vulnerabilityTrackingForm.reset();
        this.fileControl.setValue([]);
        this.classApplied = !this.classApplied;
    }

    // Método para limpiar el nombre del archivo y obtener la extensión
    cleanFileName(fileName: string): {
        cleanedName: string;
        extension: string;
    } {
        // Expresión regular para detectar UUID al final del archivo
        const uuidRegex =
            /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
        // Separar el nombre antes y después del UUID
        const parts = fileName.split(uuidRegex);

        if (parts.length > 1) {
            const cleanedNameWithPath = parts[0].trim(); // Nombre sin UUID
            const myArr = cleanedNameWithPath.split('/');
            const cleanedName = myArr[myArr.length - 1];
            const extension = cleanedNameWithPath.split('.').pop() || ''; // Extensión del archivo
            return { cleanedName, extension };
        } else {
            // Si no hay UUID, devolver el nombre original y su extensión
            const myArr = fileName.split('/');
            const cleanedName = myArr[myArr.length - 1];
            const extension = fileName.split('.').pop() || '';
            return { cleanedName, extension };
        }
    }

    // Add VulnerabilityTracking
    addVulnerabilityTracking() {
        // Disable the form
        this.vulnerabilityTrackingForm.disable();
        this.isWorking = true;
        // New VulnerabilityTracking
        const vulnerabilityTracking = {
            task: this.vulnerabilityTrackingForm.get('task')?.value,
            note: this.vulnerabilityTrackingForm.get('note')?.value,
            taskType: this.vulnerabilityTrackingForm.get('taskType')?.value,
            state: false,
            evidenceFile: this.vulnerabilityTrackingForm.get('file')?.value
                ? this.vulnerabilityTrackingForm.get('file')?.value[0].name
                : '',
            vulnerabilityAssessmentResult: {
                id: this.vulnerabilityAssessmentResultId,
            },
            trackingCreationDate: this.vulnerabilityTrackingForm.get(
                'trackingCreationDate'
            )?.value,
            trackingResolutionDate: this.vulnerabilityTrackingForm.get(
                'trackingResolutionDate'
            )?.value,
        };
        const formData = new FormData();
        // Append JSON data as a blob to maintain proper JSON format
        const jsonBlob = new Blob([JSON.stringify(vulnerabilityTracking)], {
            type: 'application/json',
        });

        formData.append('vulnerabilityTracking', jsonBlob);

        if (this.vulnerabilityTrackingForm.get('file')?.value) {
            formData.append(
                'evidence',
                this.vulnerabilityTrackingForm.get('file')?.value[0]
            );
        }

        this.apiService.addVulnerabilityTracking(formData).subscribe({
            next: (value) => {
                this.classApplied = !this.classApplied;
                this.apiService
                    .getVulnerabilityTrackingByVulnerabilityResult(
                        this.vulnerabilityAssessmentResultId,
                        this.paginator.pageIndex,
                        this.paginator.pageSize,
                        this.sort.active,
                        this.sort.direction
                    )
                    .subscribe((response: VulnerabilityTrackingResponse) => {
                        // Flip flag to show that loading has finished.
                        this.isLoadingResults = false;
                        if (response) {
                            this.totalItems = response.totalElements;
                            this.dataSource.data =
                                response.vulnerabilitiesTracking;
                        } else {
                            this.totalItems = 0;
                            this.dataSource.data = [];
                        }
                    });
            },
            error: (error) => {
                let errMsg: string = error;
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                    data: {
                        title: 'ERROR',
                        message: errMsg,
                    },
                });
            },
            complete: () => {
                this.vulnerabilityTrackingForm.reset();
                this.fileControl.setValue([]);
                this.vulnerabilityTrackingForm.enable();
                this.isWorking = false;
            },
        });
    }

    // Edit VulnerabilityTracking
    editVulnerabilityTracking() {
        // Disable the form
        this.vulnerabilityTrackingForm.disable();
        this.isWorking = true;
        this.apiService
            .getVulnerabilityTrackingById(this.vulnerabilityTrackingId)
            .subscribe((data: VulnerabilityTrackingResponse) => {
                if (data.vulnerabilityTracking) {
                    let vulnerabilityTracking: VulnerabilityTracking =
                        data.vulnerabilityTracking;
                    vulnerabilityTracking.task =
                        this.vulnerabilityTrackingForm.get('task')?.value;
                    vulnerabilityTracking.note =
                        this.vulnerabilityTrackingForm.get('note')?.value;
                    vulnerabilityTracking.taskType =
                        this.vulnerabilityTrackingForm.get('taskType')?.value;
                    vulnerabilityTracking.trackingCreationDate =
                        this.vulnerabilityTrackingForm.get(
                            'trackingCreationDate'
                        )?.value;
                    vulnerabilityTracking.trackingResolutionDate =
                        this.vulnerabilityTrackingForm.get(
                            'trackingResolutionDate'
                        )?.value;
                    this.apiService
                        .patchVulnerabilityTracking(
                            this.vulnerabilityTrackingId,
                            vulnerabilityTracking
                        )
                        .subscribe({
                            next: (value: VulnerabilityTrackingResponse) => {
                                console.log(value);
                                this.vulnerabilityTrackingId = '';
                                this.classApplied = !this.classApplied;
                                this.apiService
                                    .getVulnerabilityTrackingByVulnerabilityResult(
                                        this.vulnerabilityAssessmentResultId,
                                        this.paginator.pageIndex,
                                        this.paginator.pageSize,
                                        this.sort.active,
                                        this.sort.direction
                                    )
                                    .subscribe(
                                        (
                                            response: VulnerabilityTrackingResponse
                                        ) => {
                                            // Flip flag to show that loading has finished.
                                            this.isLoadingResults = false;
                                            if (response) {
                                                this.totalItems =
                                                    response.totalElements;
                                                this.dataSource.data =
                                                    response.vulnerabilitiesTracking;
                                            } else {
                                                this.totalItems = 0;
                                                this.dataSource.data = [];
                                            }
                                        }
                                    );
                            },
                            error: (error) => {
                                let errMsg: string = error;
                                const dialogRef = this.dialog.open(
                                    AlertDialogComponent,
                                    {
                                        data: {
                                            title: 'ERROR',
                                            message: errMsg,
                                        },
                                    }
                                );
                                this.vulnerabilityTrackingId = '';
                            },
                            complete: () => {
                                this.vulnerabilityTrackingForm.reset();
                                this.fileControl.setValue([]);
                                this.vulnerabilityTrackingForm.enable();
                                this.isWorking = false;
                            },
                        });
                } else {
                    const dialogRef = this.dialog.open(AlertDialogComponent, {
                        data: {
                            title: 'ERROR',
                            message: 'no-data-found',
                        },
                    });
                }
            });
    }

    // onClick
    onClick(id: string) {
        let changed = false;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                action: 'change',
                component: 'state',
                content: 'content-change-state',
                buttonText: {
                    ok: 'yes',
                    cancel: 'no',
                },
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                console.log('Change State: ' + id);
                this.apiService
                    .getVulnerabilityTrackingById(id)
                    .subscribe((data: VulnerabilityTrackingResponse) => {
                        if (data.vulnerabilityTracking) {
                            let vulnerabilityTracking: VulnerabilityTracking =
                                data.vulnerabilityTracking;
                            vulnerabilityTracking.state =
                                !vulnerabilityTracking.state;
                            this.apiService
                                .patchVulnerabilityTracking(
                                    id,
                                    data.vulnerabilityTracking
                                )
                                .subscribe({
                                    next: (
                                        value: VulnerabilityTrackingResponse
                                    ) => {
                                        console.log(value);
                                        this.apiService
                                            .getVulnerabilityTrackingByVulnerabilityResult(
                                                this
                                                    .vulnerabilityAssessmentResultId,
                                                this.paginator.pageIndex,
                                                this.paginator.pageSize,
                                                this.sort.active,
                                                this.sort.direction
                                            )
                                            .subscribe(
                                                (
                                                    response: VulnerabilityTrackingResponse
                                                ) => {
                                                    // Flip flag to show that loading has finished.
                                                    this.isLoadingResults =
                                                        false;
                                                    if (response) {
                                                        this.totalItems =
                                                            response.totalElements;
                                                        this.dataSource.data =
                                                            response.vulnerabilitiesTracking;
                                                    } else {
                                                        this.totalItems = 0;
                                                        this.dataSource.data =
                                                            [];
                                                    }
                                                }
                                            );
                                        changed = true;
                                    },
                                    error: (error) => {
                                        let errMsg: string = error;
                                        const dialogRef = this.dialog.open(
                                            AlertDialogComponent,
                                            {
                                                data: {
                                                    title: 'ERROR',
                                                    message: errMsg,
                                                },
                                            }
                                        );
                                    },
                                });
                        } else {
                            const dialogRef = this.dialog.open(
                                AlertDialogComponent,
                                {
                                    data: {
                                        title: 'ERROR',
                                        message: 'no-data-found',
                                    },
                                }
                            );
                        }
                    });
            }
        });
        return changed;
    }

    // Charge data to edit evidence
    edit(id: string) {
        this.vulnerabilityTrackingId = id;
        const vulnerabilityTracking = this.dataSource.data.find(
            ({ id }) => id === this.vulnerabilityTrackingId
        );
        if (vulnerabilityTracking) {
            this.vulnerabilityTrackingForm.patchValue({
                task: vulnerabilityTracking.task,
            });
            this.vulnerabilityTrackingForm.patchValue({
                note: vulnerabilityTracking.note,
            });
            this.vulnerabilityTrackingForm.patchValue({
                taskType: vulnerabilityTracking.taskType,
            });
            this.vulnerabilityTrackingForm.patchValue({
                trackingCreationDate:
                    vulnerabilityTracking.trackingCreationDate,
            });
            this.vulnerabilityTrackingForm.patchValue({
                trackingResolutionDate:
                    vulnerabilityTracking.trackingResolutionDate,
            });
            this.fileControl.setValue([]);
            this.classApplied = !this.classApplied;
        }
    }

    // Delete Management
    delete(id: string) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                action: 'delete',
                component: 'Management',
                content: 'content-delete',
                buttonText: {
                    ok: 'yes',
                    cancel: 'no',
                },
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                console.log('Delete Management: ' + id);
                this.apiService.deleteVulnerabilityTracking(id).subscribe({
                    next: (value: VulnerabilityTrackingResponse) => {
                        console.log(value);
                        this.apiService
                            .getVulnerabilityTrackingByVulnerabilityResult(
                                this.vulnerabilityAssessmentResultId,
                                this.paginator.pageIndex,
                                this.paginator.pageSize,
                                this.sort.active,
                                this.sort.direction
                            )
                            .subscribe(
                                (response: VulnerabilityTrackingResponse) => {
                                    // Flip flag to show that loading has finished.
                                    this.isLoadingResults = false;
                                    if (response) {
                                        this.totalItems =
                                            response.totalElements;
                                        this.dataSource.data =
                                            response.vulnerabilitiesTracking;
                                    } else {
                                        this.totalItems = 0;
                                        this.dataSource.data = [];
                                    }
                                }
                            );
                    },
                    error: (error) => {
                        let errMsg: string = error;
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: errMsg,
                                },
                            }
                        );
                    },
                });
            }
        });
    }
}
